import { useCallback, useRef, useEffect, useMemo } from 'react'
import { saveAs } from 'file-saver'
import { DownloadType } from 'components/shared/DownloadPanel'
import useDownloadsSlice from 'redux/slices/hooks/useDownloadsSlice'
import { useNotificationSlice } from 'redux/slices/hooks'
import useFileDownload from './useFileDownload'

export interface DownloadConfig {
  type: DownloadType
  fileName: string
  runDetails?: string
  runId?: string
}

export interface SyncDownloadConfig extends DownloadConfig {
  /**
   * Function that generates the file content synchronously
   * Returns a Blob or string to be downloaded
   */
  generateFile: () => Blob | string | Promise<Blob | string>
}

export interface AsyncDownloadConfig extends DownloadConfig {
  /**
   * Function that initiates the download process and returns a task ID
   */
  initiateDownload: () => Promise<string>
  /**
   * Function that polls for the download status
   * Returns the status and download URL when ready
   */
  pollStatus: (taskId: string) => Promise<{
    status: 'pending' | 'ready' | 'error'
    downloadUrl?: string
    errorMessage?: string
  }>
  /**
   * Polling interval in milliseconds (default: 5000)
   */
  pollInterval?: number
  /**
   * Maximum number of polling attempts (default: 60)
   */
  maxPollAttempts?: number
}

interface UseEnhancedDownloadReturn {
  /**
   * Trigger a synchronous download (file is generated client-side)
   */
  triggerSyncDownload: (config: SyncDownloadConfig) => Promise<void>
  /**
   * Trigger an asynchronous download (file is prepared server-side)
   */
  triggerAsyncDownload: (config: AsyncDownloadConfig) => Promise<void>
}

/**
 * Centralized hook for all download functionality using the Enhanced Download UX
 *
 * Supports two types of downloads:
 * 1. Synchronous: File is generated client-side (CSV, TSV, JSON, ZIP)
 * 2. Asynchronous: File is prepared server-side (Embeddings, Images, etc.)
 *
 * Features:
 * - Centralized download tracking in Redux
 * - Auto-download support
 * - Filename editing
 * - Progress tracking
 * - Error handling
 * - Non-dismissible dialogs during download
 */
const useGlobalDownloadTrigger = (): UseEnhancedDownloadReturn => {
  const { addDownloadItem, updateDownloadItem, autoDownload, downloads } = useDownloadsSlice()
  const { displayNotification } = useNotificationSlice()
  const { downloadFile } = useFileDownload()

  // Keep a ref to the current downloads for access in closures
  const downloadsRef = useRef<typeof downloads>([])

  // Update the ref whenever downloads change
  useEffect(() => {
    downloadsRef.current = downloads
  }, [downloads])

  /**
   * Trigger a synchronous download
   */
  const triggerSyncDownload = useCallback(
    async (config: SyncDownloadConfig) => {
      const { type, fileName, generateFile, runDetails, runId } = config
      const downloadId = `${type}-${Date.now()}`

      try {
        // Add download item to global state
        addDownloadItem({
          id: downloadId,
          type,
          fileName,
          status: 'preparing',
          progress: 0,
          elapsedTime: 0,
          startTime: Date.now(),
          runDetails,
          runId,
        })

        // Generate the file
        const fileContent = await generateFile()

        // Create blob if string is provided
        const blob =
          typeof fileContent === 'string'
            ? new Blob([fileContent], { type: 'text/plain' })
            : fileContent

        // Create a temporary URL for the blob
        const blobUrl = window.URL.createObjectURL(blob)

        // Update status to ready
        updateDownloadItem(downloadId, {
          status: 'ready',
          downloadUrl: blobUrl,
          progress: 100,
        })

        // Auto-download if enabled
        if (autoDownload) {
          setTimeout(async () => {
            const currentDownloadItem = downloadsRef.current.find((d) => d.id === downloadId)
            const currentFileName = currentDownloadItem?.fileName || fileName

            // Update status to downloading
            updateDownloadItem(downloadId, { status: 'downloading' })

            try {
              // For sync downloads, we can download directly using saveAs
              saveAs(blob, currentFileName)

              // Mark as completed
              updateDownloadItem(downloadId, { status: 'completed' })

              displayNotification({
                message: 'Download completed successfully.',
                type: 'success',
                timeout: 3000,
              })
            } catch (error) {
              console.error('Auto-download failed:', error)
              updateDownloadItem(downloadId, {
                status: 'error',
                errorMessage: error instanceof Error ? error.message : 'Download failed',
              })
            }
          }, 1000)
        }
      } catch (error) {
        console.error('File generation failed:', error)
        updateDownloadItem(downloadId, {
          status: 'error',
          errorMessage: error instanceof Error ? error.message : 'File generation failed',
        })

        displayNotification({
          message: 'Download failed. Please try again.',
          type: 'error',
          timeout: 5000,
        })
      }
    },
    [addDownloadItem, updateDownloadItem, autoDownload, displayNotification]
  )

  /**
   * Trigger an asynchronous download
   */
  const triggerAsyncDownload = useCallback(
    async (config: AsyncDownloadConfig) => {
      const {
        type,
        fileName,
        initiateDownload,
        pollStatus,
        pollInterval = 5000,
        maxPollAttempts = 60,
        runDetails,
        runId,
      } = config
      const downloadId = `${type}-${Date.now()}`

      try {
        // Add download item to global state
        addDownloadItem({
          id: downloadId,
          type,
          fileName,
          status: 'preparing',
          progress: 0,
          elapsedTime: 0,
          startTime: Date.now(),
          runDetails,
          runId,
        })

        // Initiate the download process
        const taskId = await initiateDownload()

        // Poll for completion
        let attempts = 0
        const pollIntervalId = setInterval(async () => {
          attempts += 1

          try {
            const statusResponse = await pollStatus(taskId)

            if (statusResponse.status === 'ready' && statusResponse.downloadUrl) {
              clearInterval(pollIntervalId)
              updateDownloadItem(downloadId, {
                status: 'ready',
                downloadUrl: statusResponse.downloadUrl,
                progress: 100,
              })

              // Auto-download if enabled
              if (autoDownload && statusResponse.downloadUrl) {
                setTimeout(async () => {
                  const currentDownloadItem = downloadsRef.current.find((d) => d.id === downloadId)
                  const currentFileName = currentDownloadItem?.fileName || fileName

                  // Update status to downloading
                  updateDownloadItem(downloadId, { status: 'downloading' })

                  try {
                    // We know downloadUrl is defined because we checked above
                    const url = statusResponse.downloadUrl
                    if (url) {
                      await downloadFile(url, currentFileName, {
                        onSuccess: () => {
                          updateDownloadItem(downloadId, { status: 'completed' })
                        },
                        onError: (error) => {
                          console.error('Auto-download failed:', error)
                          updateDownloadItem(downloadId, {
                            status: 'error',
                            errorMessage: error.message,
                          })
                        },
                      })
                    }
                  } catch (error) {
                    console.error('Auto-download failed:', error)
                    updateDownloadItem(downloadId, {
                      status: 'error',
                      errorMessage: error instanceof Error ? error.message : 'Download failed',
                    })
                  }
                }, 2000)
              }

              displayNotification({
                message: 'Your file is ready for download.',
                type: 'success',
                timeout: 5000,
              })
            } else if (statusResponse.status === 'error') {
              clearInterval(pollIntervalId)
              updateDownloadItem(downloadId, {
                status: 'error',
                errorMessage: statusResponse.errorMessage || 'Download preparation failed',
              })

              displayNotification({
                message: 'Download failed. Please try again.',
                type: 'error',
                timeout: 5000,
              })
            } else if (attempts >= maxPollAttempts) {
              clearInterval(pollIntervalId)
              updateDownloadItem(downloadId, {
                status: 'error',
                errorMessage: 'Download preparation timed out',
              })

              displayNotification({
                message: 'Download timed out. Please try again.',
                type: 'error',
                timeout: 5000,
              })
            }
          } catch (error) {
            clearInterval(pollIntervalId)
            updateDownloadItem(downloadId, {
              status: 'error',
              errorMessage: error instanceof Error ? error.message : 'Unknown error occurred',
            })

            displayNotification({
              message: 'Download failed. Please try again.',
              type: 'error',
              timeout: 5000,
            })
          }
        }, pollInterval)
      } catch (error) {
        updateDownloadItem(downloadId, {
          status: 'error',
          errorMessage: error instanceof Error ? error.message : 'Failed to initiate download',
        })

        displayNotification({
          message: 'Download failed. Please try again.',
          type: 'error',
          timeout: 5000,
        })
      }
    },
    [
      addDownloadItem,
      updateDownloadItem,
      autoDownload,
      displayNotification,
      downloadFile,
    ]
  )

  return useMemo(
    () => ({
      triggerSyncDownload,
      triggerAsyncDownload,
    }),
    [triggerSyncDownload, triggerAsyncDownload]
  )
}

export default useGlobalDownloadTrigger
